import { supabase, run } from './db';

/**
 * Acesso de plataforma: o que o painel /admin usa.
 *
 * Arquivo separado de `labs.js` de propósito. Ali é o laboratório operando a
 * si mesmo; aqui é a operação do produto olhando todos os laboratórios. Ver os
 * dois misturados num arquivo só esconderia, na hora do code review, qual
 * consulta atravessa o isolamento entre clientes.
 *
 * DUAS REGRAS VALEM PARA TUDO NESTE ARQUIVO:
 *
 * 1. NADA passa por `cached()`. Administração precisa do dado de agora — e no
 *    caso de `is_super_admin`, ler de uma cópia local seria decidir permissão
 *    a partir de dado velho.
 *
 * 2. Quem autoriza é a RLS, não este arquivo. A policy "super admin ve tudo"
 *    já existe nas tabelas envolvidas; para qualquer outro perfil estas mesmas
 *    consultas devolvem o recorte dele, ou nada. A guarda na tela é conforto,
 *    não segurança.
 */

/**
 * Quem está logado é super admin?
 *
 * Consulta direta, sem `fetchProfile` — aquele é cacheado, e cache aqui é
 * leitura obsoleta com consequência de permissão.
 */
export const souSuperAdmin = async () => {
  const { data, error } = await supabase
    .from('profiles').select('is_super_admin')
    .eq('id', (await supabase.auth.getSession()).data.session?.user?.id || '')
    .maybeSingle();
  if (error) throw error;
  return data?.is_super_admin === true;
};

/**
 * Todos os laboratórios, INCLUSIVE os arquivados.
 *
 * `listAllLaboratories` (labs.js) não serve por dois motivos: filtra
 * `status = 'active'` e é cacheada. Lá está certo — é o seletor de trabalho.
 * Aqui, um laboratório arquivado que sumisse da lista seria um cliente que
 * ninguém consegue reativar pela tela.
 */
export const listarLaboratorios = () =>
  run(supabase.from('laboratories').select('*').order('name'));

export const listarContratos = () =>
  run(supabase.from('lab_subscriptions').select('*'));

export const salvarContrato = (labId, campos) =>
  run(supabase.from('lab_subscriptions').update(campos).eq('laboratory_id', labId));

export const listarUsoAtual = () =>
  run(supabase.from('vw_uso_atual_laboratorio').select('*'));

export const listarUsoMensal = (labId) =>
  run(supabase
    .from('vw_uso_mensal_laboratorio')
    .select('*')
    .eq('laboratory_id', labId)
    .order('mes', { ascending: false })
    .limit(12));

/** Nome e e-mail de quem é dono, para a lista não mostrar só um uuid. */
export const listarPerfis = (ids) =>
  ids.length === 0 ? Promise.resolve([])
    : run(supabase.from('profiles').select('id, full_name, email').in('id', ids));

/**
 * Cria o laboratório SEM dono.
 *
 * O ovo e a galinha: `admin-create-user` precisa de um `laboratory_id` para
 * criar o primeiro admin, mas o laboratório gostaria de nascer apontando para
 * essa pessoa, que ainda não existe. `owner_id` é nullable — então nasce sem
 * dono e ganha um no fim.
 *
 * Por que NÃO usar o super admin como dono temporário: `atualizar_membro`
 * trata o dono como "quem responde pela assinatura e pela cobrança" e o
 * protege de rebaixamento. Apontar isso para a conta de plataforma faria dela
 * o membro intocável do laboratório do cliente — o oposto do desejado. E se a
 * transferência falhasse no meio, esse estado errado ficaria em produção. Com
 * nulo, o estado intermediário é apenas "ainda sem dono", que é verdade.
 *
 * Conferido antes de escrever isto: a policy de INSERT de `laboratories` é
 * `owner_id = auth.uid()`, mas a do super admin é permissiva e se soma com OR
 * — inclusive no `with check`. Um usuário comum tentando o mesmo é recusado.
 */
export const criarLaboratorio = ({ name, description = null, sebrae = false, sebrae_responsavel = null }) =>
  run(supabase.from('laboratories')
    .insert([{ name, description, sebrae, sebrae_responsavel, owner_id: null, status: 'active' }])
    .select().single());

/**
 * SEBRAE e responsável pelo projeto. Vivem em `laboratories`, não no contrato:
 * a equipe precisa ler o nome para pô-lo nos relatórios de visita.
 */
export const salvarSebraeDoLaboratorio = (labId, { sebrae, sebrae_responsavel }) =>
  run(supabase.from('laboratories')
    .update({ sebrae: !!sebrae, sebrae_responsavel: sebrae ? sebrae_responsavel : null })
    .eq('id', labId));

export const definirDono = (labId, userId) =>
  run(supabase.from('laboratories').update({ owner_id: userId }).eq('id', labId));

/** Desfaz um laboratório recém-criado que ainda não tem nada dentro. */
export const apagarLaboratorioVazio = async (labId) => {
  await run(supabase.from('lab_members').delete().eq('laboratory_id', labId));
  await run(supabase.from('lab_subscriptions').delete().eq('laboratory_id', labId));
  return run(supabase.from('laboratories').delete().eq('id', labId));
};

// ── Regras puras, testáveis ───────────────────────────────────────────────

/**
 * Situação comercial de um contrato.
 *
 * A regra de "em dia" é a MESMA de `emitir-licenca` (`!expires_at ||
 * expires_at >= hoje`). Está isolada aqui porque é o que a tela mais mostra e
 * o que mais fácil sai de sincronia com a Edge Function — se as duas
 * divergirem, o painel diz "ativo" para um cliente que o servidor está
 * bloqueando.
 *
 * `hoje` entra como parâmetro para o teste não depender do relógio.
 */
export const situacaoDoContrato = (contrato, hoje = new Date()) => {
  if (!contrato) return 'sem-contrato';
  if (!contrato.active) return 'inativo';
  if (!contrato.expires_at) return 'ativo';

  const dia = hoje.toISOString().slice(0, 10);
  if (contrato.expires_at < dia) return 'vencido';

  const faltam = Math.round(
    (new Date(`${contrato.expires_at}T00:00:00Z`) - new Date(`${dia}T00:00:00Z`))
    / 86400000);
  return faltam <= 30 ? 'vencendo' : 'ativo';
};

/** Ordem de atenção: o que está errado primeiro. */
const PESO = {
  'sem-contrato': 0, inativo: 1, vencido: 2, vencendo: 3, ativo: 4,
};

/**
 * Junta laboratório + contrato + uso numa linha só.
 *
 * Existe como função pura porque é o ponto mais provável de bug desta tela:
 * laboratório sem contrato e laboratório sem uso não podem sumir da lista nem
 * virar `NaN` — e é exatamente o que um join ingênuo faria.
 */
export const montarLinhas = (labs = [], contratos = [], uso = [], hoje = new Date()) => {
  const porContrato = Object.fromEntries((contratos || []).map(c => [c.laboratory_id, c]));
  const porUso      = Object.fromEntries((uso || []).map(u => [u.laboratory_id, u]));

  return (labs || []).map(lab => {
    const contrato = porContrato[lab.id] || null;
    const u        = porUso[lab.id] || {};
    return {
      ...lab,
      contrato,
      situacao:  situacaoDoContrato(contrato, hoje),
      clientes:  u.clientes ?? 0,
      servicos:  u.servicos ?? 0,
      embrioes:  u.embrioes ?? 0,
      ultimo_lancamento_em: u.ultimo_lancamento_em || null,
    };
  }).sort((a, b) => {
    const p = PESO[a.situacao] - PESO[b.situacao];
    if (p !== 0) return p;
    // Depois do problema, o mais ativo primeiro. Quem nunca lançou vai ao fim.
    return (b.ultimo_lancamento_em || '').localeCompare(a.ultimo_lancamento_em || '');
  });
};

/** Guarda só os 14 dígitos; a máscara é apresentação. */
export const cnpjDigitos = (v) => (v || '').replace(/\D/g, '').slice(0, 14);

export const cnpjComMascara = (v) => {
  const d = cnpjDigitos(v);
  if (d.length !== 14) return d;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};
